import { AccountDataSourceEnum, DexfSettingsFilterSegmentEnum } from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';
import DexfAccountService from '../../services/authentication/account-service/DexfAccountService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
const CACHES_STORE_NAME = 'accountService';
const CACHE_KEY = {
    user: 'user',
    queue: 'user-queue'
};
/**
 * This class serves as an interface for DEXF Account Service.
 */
export default class AccountService {
    /**
     * Initializes a new instance of AccountService.
     *
     * @param options Configuration options.
     */
    constructor(options) {
        this.cache = new GlobalCacheStore().getStore(CACHES_STORE_NAME);
        this.applicationName = options.applicationName;
        this.dexfConfiguration = new DexfConfiguration(options);
        this.locale = options.locale;
        this.loginOptions = options.loginOptions || {};
        this.logoutRedirectUri = options.logoutRedirectUri || '';
        this.redirectUri = options.redirectUri;
        this.settingsSegment = options.settingsSegment;
    }
    /**
     * Returns a logged in user IAccountServiceUser if the user is logged in, otherwise null.
     *
     * @param options ...
     * @param options.useSilentAuthentication Should be set to true when loading the application, to sync the logged in state with the web. Kompis will silently fetch if the user is logged in to the identity provider via a hidden iframe.
     * @returns A IAccountServiceUser if the user is logged in, otherwise null.
     */
    async getUser(options = { useSilentAuthentication: false }) {
        const cachedUser = this.getUserFromCache();
        if (cachedUser) {
            return cachedUser;
        }
        const cachedQueue = this.cache.get(CACHE_KEY.queue);
        if (cachedQueue) {
            return new Promise((resolve, reject) => cachedQueue.push({ resolve, reject }));
        }
        const newQueue = [];
        this.cache.set(CACHE_KEY.queue, newQueue);
        const service = await this.getService();
        let user = null;
        try {
            user = await service.getUser(options);
        }
        catch (error) {
            for (const item of newQueue) {
                item.reject(error);
            }
            this.cache.delete(CACHE_KEY.queue);
            throw error;
        }
        this.setUserToCache(user);
        this.cache.delete(CACHE_KEY.queue);
        for (const item of newQueue) {
            item.resolve(user);
        }
        return user;
    }
    /**
     * Initiates the user login process.
     *
     * @param options Configuration options.
     * @param options.state When set the state option will be returned when redirected back to the application after login. This can be used to get the user back to where they were in the application.
     */
    async logIn(options) {
        const state = options?.state;
        const service = await this.getService();
        const user = await service.logIn({ state });
        if (user) {
            this.setUserToCache(user);
        }
        return user;
    }
    async logOut() {
        const service = await this.getService();
        await service.logOut();
    }
    /**
     * Initializes and returns a new instance of DexfAccountService.
     *
     * @returns DexfAccountService instance.
     */
    async getService() {
        const { kompis } = await this.dexfConfiguration.getSettings();
        switch (kompis.serviceSettings.accountDataSource) {
            case AccountDataSourceEnum.disabled:
                throw new ServiceException('The accountDataSource is set to "disabled". When it is, the AccountService is not available on the market and the login functionality together with related features, should be hidden or disabled.');
            default:
                return new DexfAccountService({
                    accessTokenUrl: kompis.endpoints.dexfAccountServiceAccessTokenUrl,
                    authenticationUrl: kompis.endpoints.dexfAccountService,
                    clientId: this.getClientId(),
                    locale: this.locale,
                    loginOptions: this.loginOptions,
                    logoutUri: kompis.endpoints.dexfAccountServiceLogout,
                    logoutRedirectUri: this.logoutRedirectUri,
                    redirectUri: this.redirectUri,
                    silentAuthenticationUrl: kompis.endpoints.dexfAccountServiceSilentAuthenticationUrl
                });
        }
    }
    getClientId() {
        return this.settingsSegment && this.settingsSegment !== DexfSettingsFilterSegmentEnum.production
            ? `${this.applicationName}.${this.settingsSegment}`
            : this.applicationName;
    }
    getUserFromCache() {
        return this.cache.get(CACHE_KEY.user);
    }
    setUserToCache(user) {
        this.cache.set(CACHE_KEY.user, user);
    }
}
//# sourceMappingURL=AccountService.js.map